import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { CAREER_QUESTIONS } from "../data/blueprint_career";
import { SITE } from "../data/content";
import useReveal from "../hooks/useReveal";
import {
    QuestionCard,
    LeadCapture,
    ScoreRow,
    useSetTitle,
} from "./BlueprintPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Intro({ onStart }) {
    useReveal();
    return (
        <section className="pt-24 pb-16 lg:pb-24" data-testid="career-intro">
            <div className="max-w-4xl mx-auto px-6 lg:px-12 pt-10">
                <p className="ssh-eyebrow">Free · Self-Reflection Assessment</p>
                <h1 className="mt-6 font-serif font-light tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-[#2C2B29]">
                    Your Career Transition{" "}
                    <em className="italic text-[#A68A6B]">Blueprint.</em>
                </h1>
                <p className="mt-6 text-lg leading-[1.7] text-[#4A4947]">
                    Discover where you currently are in your career — and what
                    may be standing between you and your next direction.
                </p>
                <div className="mt-10 space-y-5 text-base lg:text-lg leading-[1.75] text-[#2C2B29] font-light">
                    <p>
                        Career transitions are not always about simply finding
                        another job. Sometimes you know something isn&apos;t
                        working but don&apos;t know what you want instead.
                    </p>
                    <p>
                        Sometimes you know what you want, but fear, financial
                        concerns, responsibilities, confidence or uncertainty
                        make it difficult to move.
                    </p>
                    <p>
                        This assessment will help you explore four areas:
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 pl-1">
                        {[
                            "Career Alignment",
                            "Career Impact",
                            "Ability to Move Forward",
                            "Readiness for Change",
                        ].map((x) => (
                            <li key={x} className="flex gap-3">
                                <span className="text-[#A68A6B]">—</span>
                                <span>{x}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="mt-10">
                    <Button
                        onClick={onStart}
                        className="ssh-btn-primary rounded-none px-9 py-7 text-xs uppercase tracking-[0.22em] inline-flex items-center gap-3"
                        data-testid="career-start"
                    >
                        <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                        Discover My Career Blueprint
                    </Button>
                </div>
                <p className="mt-10 text-xs text-[#75736F] leading-relaxed max-w-2xl border-l-2 border-[#EFEBE4] pl-4">
                    This is a self-reflection and personal-development tool. It
                    is not a psychological diagnosis, clinical assessment,
                    financial assessment, career-placement service, or
                    guarantee of career outcomes.
                </p>
            </div>
        </section>
    );
}

function ResultView({ result }) {
    const isHot = result.lead_temperature === "HOT";
    return (
        <section className="pt-24 pb-24" data-testid="career-result">
            <div className="max-w-4xl mx-auto px-6 lg:px-12 pt-10">
                <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="ssh-eyebrow"
                >
                    Your Career Transition Blueprint
                </motion.p>
                <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-6 font-serif text-2xl text-[#4A4947]"
                >
                    {result.first_name}, your current career stage is
                </motion.p>
                <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-4 font-serif font-light tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-7xl"
                    style={{ color: "#A68A6B" }}
                >
                    <em className="italic">{result.final_stage}</em>
                </motion.h1>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-12 bg-white border border-[#EFEBE4] p-8 lg:p-10"
                >
                    <p className="font-serif italic text-xl lg:text-2xl text-[#2C2B29] leading-snug border-l-2 border-[#A68A6B] pl-4">
                        {result.stage_headline}
                    </p>
                    <div className="mt-8 space-y-5">
                        {result.stage_body.map((p, i) => (
                            <p key={i} className="text-base lg:text-lg leading-[1.85] text-[#2C2B29] font-light">
                                {p}
                            </p>
                        ))}
                    </div>
                    <div className="mt-8 pt-8 border-t border-[#EFEBE4]">
                        <p className="ssh-eyebrow">Your reflection</p>
                        <p className="mt-3 font-serif text-xl lg:text-2xl italic text-[#2C2B29] leading-snug">
                            {result.stage_reflection}
                        </p>
                    </div>
                </motion.div>

                <div className="mt-16">
                    <p className="ssh-eyebrow">Your four category scores</p>
                    <div className="mt-6">
                        <ScoreRow label="Career Alignment" pct={result.alignment_percentage} valueLabel={result.alignment_label} color={result.alignment_color} />
                        <ScoreRow label="Career Impact" pct={result.impact_percentage} valueLabel={result.impact_label} color={result.impact_color} />
                        <ScoreRow label="Ability to Move Forward" pct={result.ability_percentage} valueLabel={result.ability_label} color={result.ability_color} />
                        <ScoreRow label="Readiness for Change" pct={result.readiness_percentage} valueLabel={result.readiness_label} color={result.readiness_color} />
                    </div>
                </div>

                {result.barriers && result.barriers.length > 0 && (
                    <div className="mt-16">
                        <p className="ssh-eyebrow">What may be keeping you stuck</p>
                        <div className="mt-6 space-y-6">
                            {result.barriers.map((b, i) => (
                                <div key={i} className="border-l-2 border-[#A68A6B] pl-5">
                                    <h3 className="font-serif text-xl lg:text-2xl text-[#2C2B29] leading-tight">
                                        {b.heading}
                                    </h3>
                                    <p className="mt-3 text-base leading-[1.75] text-[#4A4947] font-light">
                                        {b.text}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10% 0px" }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className={`mt-20 border p-8 lg:p-12 ${
                        isHot ? "bg-[#2C2B29] border-[#2C2B29] text-white" : "bg-[#FAF8F5] border-[#EFEBE4]"
                    }`}
                    data-testid="career-cta"
                >
                    <p className="ssh-eyebrow" style={{ color: isHot ? "#C5A880" : undefined }}>
                        {isHot ? "You said you're ready" : "A gentle next step"}
                    </p>
                    <h3 className={`mt-5 font-serif font-light tracking-tight leading-[1.05] text-3xl lg:text-4xl ${
                        isHot ? "text-white" : "text-[#2C2B29]"
                    }`}>
                        Want to explore your next{" "}
                        <em className="italic" style={{ color: isHot ? "#C5A880" : "#A68A6B" }}>
                            career direction?
                        </em>
                    </h3>
                    <p className={`mt-5 text-base lg:text-lg leading-relaxed max-w-2xl ${
                        isHot ? "text-[#d8d3c8]" : "text-[#4A4947]"
                    }`}>
                        Your Career Blueprint shows where you currently are.
                        The next step is understanding what is keeping you
                        there and which realistic options may be worth
                        exploring.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center gap-4">
                        <Link to="/contact#booking">
                            <Button className="ssh-btn-primary rounded-none px-9 py-7 text-xs uppercase tracking-[0.22em]" data-testid="career-cta-book">
                                Book a Career Clarity Session
                            </Button>
                        </Link>
                    </div>
                </motion.div>

                <div className="mt-12 text-center">
                    <Link
                        to="/blueprint"
                        className="text-xs uppercase tracking-[0.22em] text-[#A68A6B] hover:text-[#897157] inline-flex items-center gap-2"
                        data-testid="career-explore-another"
                    >
                        Explore another Blueprint
                        <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </Link>
                </div>

                <p className="mt-10 text-xs text-[#75736F] leading-relaxed max-w-2xl border-l-2 border-[#EFEBE4] pl-4">
                    This is a self-reflection and personal-development tool. It
                    is not a psychological diagnosis, clinical assessment,
                    financial assessment, career-placement service, or
                    guarantee of career outcomes.{" "}
                    <a
                        href={`https://wa.me/${SITE.whatsappRaw}`}
                        target="_blank"
                        rel="noreferrer"
                        className="underline hover:text-[#A68A6B]"
                    >
                        Reach out to Shreya
                    </a>{" "}
                    if you need to talk with someone.
                </p>
            </div>
        </section>
    );
}

export default function CareerBlueprintPage() {
    useSetTitle("Your Career Transition Blueprint · Soul Sync Healing");
    const [step, setStep] = useState("intro");
    const [qIdx, setQIdx] = useState(0);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const total = CAREER_QUESTIONS.length;
    const current = CAREER_QUESTIONS[qIdx];
    const value = current
        ? current.multi
            ? answers[`q${current.id}`] || []
            : answers[`q${current.id}`]
        : undefined;

    const canProceed = useMemo(() => {
        if (!current) return false;
        if (current.multi) return Array.isArray(value) && value.length > 0;
        return value !== undefined && value !== null && value !== "";
    }, [current, value]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [step, qIdx]);

    const setAnswer = (val) => setAnswers((s) => ({ ...s, [`q${current.id}`]: val }));

    const next = () => {
        if (!canProceed) return;
        if (qIdx < total - 1) setQIdx((i) => i + 1);
        else setStep("lead");
    };
    const back = () => {
        if (qIdx > 0) setQIdx((i) => i - 1);
        else setStep("intro");
    };

    const submit = async (lead) => {
        setSubmitting(true);
        try {
            const a = {};
            for (let i = 1; i <= 18; i++) {
                const v = answers[`q${i}`];
                a[`q${i}`] = typeof v === "number" ? v : null;
            }
            a.q19 = answers.q19 || [];
            a.q20 = answers.q20 || null;
            const { data } = await axios.post(`${API}/blueprint/career/submit`, {
                ...lead,
                answers: a,
            });
            setResult(data);
            setStep("result");
        } catch (err) {
            toast.error("Something went wrong. Please try again in a moment.");
        } finally {
            setSubmitting(false);
        }
    };

    if (step === "result" && result) return <ResultView result={result} />;
    if (step === "intro") return <Intro onStart={() => setStep("q")} />;

    return (
        <section className="pt-24 pb-24" data-testid="career-assessment">
            <div className="max-w-3xl mx-auto px-6 lg:px-12 pt-10">
                {step === "q" && current && (
                    <>
                        <QuestionCard
                            question={current}
                            value={value}
                            onChange={setAnswer}
                            index={qIdx}
                            total={total}
                        />
                        <div className="mt-12 flex items-center justify-between gap-4">
                            <Button
                                onClick={back}
                                variant="outline"
                                className="rounded-none border-[#2C2B29] bg-transparent text-[#2C2B29] hover:bg-[#2C2B29] hover:text-white px-7 py-6 text-xs uppercase tracking-[0.22em] inline-flex items-center gap-2 transition-colors"
                                data-testid="career-back"
                            >
                                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                                Back
                            </Button>
                            <Button
                                onClick={next}
                                disabled={!canProceed}
                                className="ssh-btn-primary rounded-none px-7 py-6 text-xs uppercase tracking-[0.22em] inline-flex items-center gap-2 disabled:opacity-40"
                                data-testid="career-next"
                            >
                                {qIdx < total - 1 ? "Next" : "Continue"}
                                <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                            </Button>
                        </div>
                    </>
                )}
                {step === "lead" && (
                    <>
                        <LeadCapture
                            onSubmit={submit}
                            submitting={submitting}
                            heading="Your Career Blueprint"
                            secondaryHeading="is ready."
                            subtitle="Enter your details to unlock your personalised Career Transition Blueprint."
                            buttonLabel="Show My Career Blueprint"
                        />
                        <div className="mt-8">
                            <button
                                type="button"
                                onClick={() => {
                                    setStep("q");
                                    setQIdx(total - 1);
                                }}
                                className="text-xs uppercase tracking-[0.22em] text-[#A68A6B] hover:text-[#897157] inline-flex items-center gap-2"
                                data-testid="career-lead-back"
                            >
                                <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
                                Back to questions
                            </button>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}
